import { JobQueueService, JobType, ZipJobsResponseModel } from "@app/jobs";
import { JobQueue } from "@app/jobs/job-queues/jobQueue";
import { FlyoutFooter } from "@app/shared/Flyouts";
import { ColumnConfig, SortDirections, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { FC, ReactElement, useEffect, useContext, useRef, useState } from "react";
import { UserContext } from "@app/user/UserContext";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";
import { formatJobsFlyoutAsOf } from "@app/shared/utility/formatJobsFlyoutAsOf";
import { formatJobsFlyoutDuration } from "@app/shared/utility/formatJobsFlyoutDuration";
import JobService from "@app/jobs/jobService";
import ZipProjectService from "@app/projects/zipProjectService";

interface ZipJobsFlyoutProps {
  projectId: number;
}

interface ZipJobsFlyoutTableData {
  [wafflesKeyColumnName]: string;
  asOf: string;
  county: string;
  duration: string;
  jobId: React.ReactElement;
  jobStatus: React.ReactElement;
  jobType: JobType;
  membership: string;
  queueDepthEstimate: string;
  queueName: string;
  stateName: string;
}

export const ZipJobsFlyout: FC<ZipJobsFlyoutProps> = (props: ZipJobsFlyoutProps) => {
  const jobService = useRef(new JobService()).current;
  const jobQueueService = useRef(new JobQueueService()).current;
  const zipProjectService = useRef(new ZipProjectService()).current;
  const [data, setData] = useState<ZipJobsFlyoutTableData[]>([]);
  const [timestamp, setTimestamp] = useState("");
  const [loading, setLoading] = useState(true);
  const [jobsResponse, setJobsResponse] = useState<ZipJobsResponseModel[]>([]);
  const [queuesResponse, setQueuesResponse] = useState<JobQueue[]>([]);
  const [resubmittedJobsMap, setResubmittedJobsMap] = useState(new Map<number, string>());
  const userContext = useContext(UserContext);

  // request data on mount
  useEffect(() => {
    refreshData();
  }, []);

  // update the as of timestamp
  useEffect(() => {
    const d = new Date();
    const displayString = `${d.toLocaleDateString()} at ${d.toLocaleTimeString()}`;
    setTimestamp(displayString);
  }, [data]);

  // check for updates to the resubmitted jobs map
  useEffect(() => {
    const newResubmittedJobsMap = new Map(resubmittedJobsMap);
    for (const [key, value] of resubmittedJobsMap) {
      const d = data.find((d) => d[wafflesKeyColumnName] === key.toString());
      if (!!d && d.asOf !== value) {
        newResubmittedJobsMap.delete(key);
      }
    }

    setResubmittedJobsMap(newResubmittedJobsMap);
  }, [jobsResponse]);

  // update the table data
  useEffect(() => {
    const newData: ZipJobsFlyoutTableData[] = jobsResponse.map((r) => {
      let heartbeatIcon: React.ReactNode;

      if (r.job.lastHeartbeatDate != undefined && r.job.jobStatus != "Finished" && r.job.jobStatus != "Canceled" && r.job.jobStatus != "Failed") {
        // checks if heartbeat received within last 15 minutes, which is 900,000 milliseconds
        if (Math.abs(new Date().getTime() - new Date(r.job.lastHeartbeatDate.toString()).getTime()) > 900000) {
          heartbeatIcon = <i className="fa fa-exclamation-triangle" />;
        }
      }

      const seqLink: React.ReactNode = (
        <span key={r.job.jobId}>
          <a href={r.jobUrl} target="_blank" rel="noreferrer">{r.job.jobId}</a>
          {heartbeatIcon}
        </span>
      );

      let jobStatusNode: React.ReactNode;
      const asOfString = formatJobsFlyoutAsOf(r.job.jobStatusDuration.statusAsOfDate);
      const resubmittedAsOf = resubmittedJobsMap.get(r.job.jobId);
      const disabled = !!resubmittedAsOf && resubmittedAsOf === asOfString;
      if (r.canRequeue) {
        jobStatusNode = (
          <div key={r.job.jobStatus}>
            {r.job.jobStatus}
            <RoleRestricted allowedRoles={[Roles.OPERATIONS_USER, Roles.OPERATIONS_ADMIN]}>
              <QESSelect
                label=""
                testId="jobs_panel_retry_job_select"
                disabled={loading || disabled}
                onChange={(e) => onResubmit(e.target.value, r.job.jobId, r.projectRunConfigurationId, asOfString)}
                defaultValue="0"
              >
                <option value="0">Select Retry Queue</option>
                {queuesResponse.map((option) => (
                  <option key={option.id} value={option.name}>{`${option.name}`}</option>
                ))}
              </QESSelect>
            </RoleRestricted>
          </div>)
      } else {
        jobStatusNode = (
          <div key={r.job.jobStatus}>
            {r.job.jobStatus}
          </div>)
      }

      /* oxlint-disable sort-keys */
      return {
        [wafflesKeyColumnName]: r.job.jobId.toString(),
        jobId: seqLink,
        jobType: r.job.jobType,
        membership: r.membership,
        stateName: r.stateName || "",
        county: r.county || "",
        jobStatus: jobStatusNode,
        asOf: asOfString,
        duration: formatJobsFlyoutDuration(r.job.jobStatusDuration.duration),
        queueName: r.queueName,
        queueDepthEstimate: r.queueDepthEstimate.toString() === "0" ? "---" : `${r.queueDepthEstimate.toString()}/${r.jobCount}`,
      };
      /* oxlint-enable */
    });

    setData(newData);
    setLoading(false);
  }, [jobsResponse, queuesResponse, loading, resubmittedJobsMap]);

  const refreshData = () => {
    setLoading(true);
    jobService.getZipJobStatusesByCustomProjectIdAsync(props.projectId).then((newJobsResponse) => {
      setJobsResponse(newJobsResponse);
      if (userContext.roles.includes(Roles.OPERATIONS_ADMIN)) {
        jobQueueService.getAvailableJobQueues().then((newQueuesResponse) => {
          setQueuesResponse(newQueuesResponse);
        });
      }
    });
  };

  const onResubmit = async (newQueueName: string, jobId: number, runConfigurationId: number, asOf: string) => {
    setLoading(true);
    await zipProjectService.retryZipAnalysisByJobId(newQueueName, runConfigurationId, jobId);
    const newResubmittedJobsMap = new Map(resubmittedJobsMap);
    newResubmittedJobsMap.set(jobId, asOf);
    setResubmittedJobsMap(newResubmittedJobsMap);
  }

  const customSort = (rows: ZipJobsFlyoutTableData[], column: keyof ZipJobsFlyoutTableData, direction: SortDirections) => {
    const multiplier = direction === SortDirections.Asc ? 1 : -1;
    switch (column) {
      // dates/times
      case "asOf":
      case "duration":
        return rows.sort((a, b) => (a[column] > b[column] ? 1 : -1) * multiplier);
      // elements
      case "jobType":
      case "jobId":
      case "jobStatus":
        return rows.sort((a, b) => ((a[column] as ReactElement).key > (b[column] as ReactElement).key ? 1 : -1) * multiplier);
      // strings/numbers
      case "county":
      case "membership":
      case "queueDepthEstimate":
      case "queueName":
      case "stateName":
        return rows.sort((a, b) => (a[column] > b[column] ? 1 : -1) * multiplier);
    }
  }

  return (
    <>
      <div className="d-flex justify-content-end align-items-center mb-2">
        <span>Last Refreshed: {timestamp}</span>
        <QESButton
          className="ms-3"
          qesType="primary"
          qesTestId="zip-jobs-flyout__refresh"
          disabled={loading}
          onClick={refreshData}
        >
          Refresh Data
        </QESButton>
      </div>
      <Waffles<ZipJobsFlyoutTableData>
        columns={columnConfig}
        customSort={customSort}
        data={data}
        fullWidth={true}
        loading={loading}
        testId="zip-jobs-flyout__table"
      />
      <FlyoutFooter submitButton={null} />
    </>
  );
}

const columnConfig: ColumnConfig = {
  asOf: {
    displayText: "As Of",
    testId: "as-of",
  },
  county: {
    displayText: "County",
    testId: "county",
  },
  duration: {
    displayText: "Duration",
    testId: "duration",
  },
  jobId: {
    displayText: "Job ID",
    testId: "job-id",
  },
  jobStatus: {
    displayText: "Job Status",
    testId: "job-status",
  },
  jobType: {
    displayText: "Job Type",
    testId: "job-type",
  },
  membership: {
    displayText: "Membership",
    testId: "membership",
  },
  queueDepthEstimate: {
    displayText: "Depth",
    testId: "queue-depth-estimate",
  },
  queueName: {
    displayText: "Queue Name",
    testId: "queue-name",
  },
  stateName: {
    displayText: "State",
    testId: "state-name",
  },
}